import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';

import { catchError, map, Observable, of } from 'rxjs';
import {
  AuthenticationConfiguration,
  CreateAuthenticationConfigurationRequest,
  UpdateAuthenticationConfigurationRequest,
} from '../../api/models';
import {
  AuthTypeValues,
  CreateAuthenticationConfigurationParams,
} from '../../types/types';

import {
  getByOrganizationId_3 as getEmployeeAuthenticationConfgurationFn,
  GetByOrganizationId_3$Params,
} from '../../api/fn/employee-authentication-configuration/get-by-organization-id-3';
import { createAuthenticationConfiguration_1 as createEmployeeAuthenticationConfigurationFn } from '../../api/fn/employee-authentication-configuration/create-authentication-configuration-1';
import { updateAuthenticationConfiguration_1 as updateEmployeeAuthenticationConfigurationFn } from '../../api/fn/employee-authentication-configuration/update-authentication-configuration-1';
import {
  deleteAuthenticationConfiguration_1 as deleteEmployeeAuthenticationConfigFn,
  DeleteAuthenticationConfiguration_1$Params,
} from '../../api/fn/employee-authentication-configuration/delete-authentication-configuration-1';

@Injectable({ providedIn: 'root' })
export class EmployeeAuthenticationService {
  constructor(private apiService: ApiService) {}

  getAuthenticationConfig(
    params: GetByOrganizationId_3$Params
  ): Observable<AuthenticationConfiguration | undefined> {
    return this.apiService
      .invoke(getEmployeeAuthenticationConfgurationFn, params)
      .pipe(map((data) => data.authenticationConfiguration));
  }

  createAuthenticationConfig(
    params: CreateAuthenticationConfigurationParams
  ): Observable<boolean> {
    const { organizationId, authType, authConfigValues, secretName } = params;
    const authConfig: AuthenticationConfiguration = {};

    if (authType === AuthTypeValues.OAUTH2) {
      authConfig.oAuth2Configuration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
      };
    } else if (authType === AuthTypeValues.ENTRAID) {
      authConfig.entraIdConfiguration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        logoutEndpoint: authConfigValues.logout_endpoint ?? '',
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
        externalUserIdClaimName: authConfigValues.ext_userId_claim,
        selfDiscoveryEndpoint: authConfigValues.self_discovery_endpoint,
        tenantId: authConfigValues.tenantId!,
      };
    } else if (authType === AuthTypeValues.OPENIDCONNECT) {
      authConfig.openIdConnectConfiguration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        logoutEndpoint: authConfigValues.logout_endpoint ?? '',
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
        selfDiscoveryEndpoint: authConfigValues.self_discovery_endpoint,
        externalUserIdClaimName: authConfigValues.ext_userId_claim,
      };
    }

    const authConfigBody: CreateAuthenticationConfigurationRequest = {
      configuration: authConfig,
    };

    return this.apiService
      .invoke(createEmployeeAuthenticationConfigurationFn, {
        organizationId: organizationId,
        body: authConfigBody,
      })
      .pipe(
        map(() => true),
        catchError((e) => {
          return of(false);
        })
      );
  }

  updateAuthenticationConfig(
    params: CreateAuthenticationConfigurationParams
  ): Observable<boolean> {
    const { organizationId, authType, authConfigValues, secretName } = params;
    const authConfig: AuthenticationConfiguration = {};

    if (authType === AuthTypeValues.OAUTH2) {
      authConfig.oAuth2Configuration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
      };
    } else if (authType === AuthTypeValues.ENTRAID) {
      authConfig.entraIdConfiguration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        logoutEndpoint: authConfigValues.logout_endpoint ?? '',
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
        externalUserIdClaimName: authConfigValues.ext_userId_claim,
        selfDiscoveryEndpoint: authConfigValues.self_discovery_endpoint,
        tenantId: authConfigValues.tenantId!,
      };
    } else if (authType === AuthTypeValues.OPENIDCONNECT) {
      authConfig.openIdConnectConfiguration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        logoutEndpoint: authConfigValues.logout_endpoint ?? '',
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
        selfDiscoveryEndpoint: authConfigValues.self_discovery_endpoint,
        externalUserIdClaimName: authConfigValues.ext_userId_claim,
      };
    }

    const authConfigBody: UpdateAuthenticationConfigurationRequest = {
      configuration: authConfig,
    };

    return this.apiService
      .invoke(updateEmployeeAuthenticationConfigurationFn, {
        organizationId: organizationId,
        body: authConfigBody,
      })
      .pipe(
        map(() => true),
        catchError((e) => {
          return of(false);
        })
      );
  }

  deleteAuthenticationConfig(
    params: DeleteAuthenticationConfiguration_1$Params
  ): Observable<boolean> {
    return this.apiService
      .invoke(deleteEmployeeAuthenticationConfigFn, params)
      .pipe(
        map(() => true),
        catchError((e) => {
          return of(false);
        })
      );
  }
}
